import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Clock, Vote as VoteIcon, Sparkles, Loader2 } from "lucide-react";
import api, { startCheckout, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VoteButton from "@/components/VoteButton";

export default function Vote() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [packs, setPacks] = useState(null);
  const [buying, setBuying] = useState("");

  const load = () => api.get("/leaderboard", { params: { limit: 56 } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/payments/packages").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    if (!user) { toast.error("Please sign in to buy vote packs"); return; }
    setBuying(lookup_key);
    try { await startCheckout(lookup_key); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout"); setBuying(""); }
  };

  const maxVotes = items[0]?.votes || 1;

  return (
    <div className="section py-12 sm:py-16" data-testid="vote-page">
      <p className="kicker mb-2">Cast Your Support</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Vote Now</h1>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          { Icon: VoteIcon, t: "Free Daily Voting", d: "Every fan gets 10 free votes each day. No purchase required." },
          { Icon: ShieldCheck, t: "Verified & Fair", d: "Every vote is server-validated and protected by our anti-fraud engine." },
          { Icon: Clock, t: stats?.voting_open ? "Voting Open" : "Voting Closed", d: stats?.phase || "Continental voting phase." },
        ].map(({ Icon, t, d }) => (
          <div key={t} className="card-lux p-5">
            <Icon className="mb-3 h-5 w-5 text-gold" />
            <h3 className="text-lg text-amber-50">{t}</h3>
            <p className="mt-1 text-sm text-amber-100/60">{d}</p>
          </div>
        ))}
      </div>

      {/* Vote packs */}
      {packs?.paid_voting_enabled && (
        <div className="mt-8" data-testid="vote-packs">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gold" />
            <h2 className="text-xl text-amber-50">Boost with a Vote Pack</h2>
            {user && <span className="ml-auto rounded-full border border-gold/25 px-3 py-1 font-mono text-xs text-gold">Credits: {user.vote_credits ?? 0}</span>}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {packs.vote_packs.map((p) => (
              <div key={p.lookup_key} className="card-lux p-6 text-center" data-testid={`vote-pack-${p.lookup_key}`}>
                <p className="font-mono text-4xl gold-text">{p.votes}</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">extra votes</p>
                <p className="mt-3 font-display text-lg text-amber-50">{p.price}</p>
                <button onClick={() => buy(p.lookup_key)} disabled={buying === p.lookup_key} className="btn-gold mt-4 w-full inline-flex items-center justify-center gap-2" data-testid={`buy-${p.lookup_key}`}>
                  {buying === p.lookup_key && <Loader2 className="h-4 w-4 animate-spin" />} Buy Pack
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Vote credits are spent automatically after your free daily votes are used. Test card: 4242 4242 4242 4242.</p>
        </div>
      )}

      <div className="mt-10 card-lux divide-y divide-gold/10" data-testid="vote-leaderboard">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-4 sm:gap-5">
            <span className={`font-mono w-7 text-center text-lg ${c.rank <= 3 ? "gold-text" : "text-muted-foreground"}`}>{c.rank}</span>
            <Link to={`/candidates/${c.slug}`} className="flex min-w-0 flex-1 items-center gap-3">
              <img src={c.portrait_url} alt={c.name} loading="lazy" className="h-12 w-12 rounded-full object-cover object-top" />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2"><span className="truncate text-sm text-amber-50">{c.name}</span><span>{c.flag}</span></span>
                <span className="mt-1 block h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-obsidian-600">
                  <span className="block h-full gold-bg" style={{ width: `${Math.max((c.votes / maxVotes) * 100, 4)}%` }} />
                </span>
              </span>
            </Link>
            <span className="hidden font-mono text-sm gold-text tabular-nums sm:block">{c.votes.toLocaleString()}</span>
            <VoteButton candidate={c} size="sm" label="Vote" onVoted={load} />
          </div>
        ))}
      </div>
    </div>
  );
}
